import propsToCss from '@styled-system/css'
import React, { ForwardedRef, forwardRef, ReactNode } from 'react'
import ArrowRightDarkIcon from '../../assets/images/icons/arrow-right-dark.svg'
import ArrowRightIcon from '../../assets/images/icons/arrow-right.svg'
import { transformResponsiveProp } from '../../styles/helpers'
import { BreakpointProp } from '../../styles/stylePropTypes'
import theme, { fontVariants } from '../../styles/theme'
import Box, { BoxProps } from '../primitives/Box'
import Row from '../primitives/Row'

export const buttonSizes = ['small', 'medium', 'large', 'xlarge'] as const
export type Size = (typeof buttonSizes)[number]

export const buttonVariants = [
  'primary',
  'secondary',
  'secondaryWithBorder',
  'lightSecondaryWithBorder',
  'dark',
  'ghost',
  'lightGhost',
  'link',
  'leadLink',
  'hoverHighlight',
  'darkMenuLink',
  'darkHoverHighlight',
  'underlineLink',
  'basicLink',
  'muted',
  'plain',
] as const
export type Variant = (typeof buttonVariants)[number]

type CommonBoxProps = BoxProps<'button'> & BoxProps<'a'>

export type ButtonProps = Omit<CommonBoxProps, 'size' | 'ref'> & {
  href?: string
  variant?: Variant
  disabled?: boolean
  size?: BreakpointProp<Size>
  children?: ReactNode
  block?: boolean
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void
  hoverProps?: CommonBoxProps
  arrow?: boolean
  rightCaret?: boolean
}

const commonProps: CommonBoxProps = {
  cursor: 'pointer',
}

const buttonResetProps: CommonBoxProps = {
  //@ts-ignore -- This works, our box typings are just off.
  backgroundColor: 'transparent',
  border: 0,
  fontFamily: 'inherit',
  //@ts-ignore -- This works, our box typings are just off.
  fontSize: 'inherit',
  //@ts-ignore -- This works, our box typings are just off.
  lineHeight: 'inherit',
  padding: 0,
}

const anchorProps: CommonBoxProps = {
  ...buttonResetProps,
  fontWeight: 500,
  fontFamily: 'FactorA',
  display: 'inline-block',
}

const buttonProps: CommonBoxProps = {
  font: 'buttonLabel',
  display: 'inline-flex',
  alignItems: 'center',
  alignContent: 'center',
  whiteSpace: 'nowrap',
  //@ts-ignore -- This works, our box typings are just off.
  border: 0,
  justifyContent: 'center',
  lineHeight: '120%',
}

const buttonSizeStyles: Record<Size, CommonBoxProps> = {
  small: {
    fontSize: 14,
    borderRadius: 4,
    paddingX: 10,
    height: 32,
  },
  medium: {
    fontSize: 16,
    paddingX: 16,
    borderRadius: 4,
    height: 40,
  },
  large: {
    fontSize: 20,
    paddingX: 24,
    borderRadius: 8,
    height: 56,
  },
  xlarge: {
    fontSize: 24,
    paddingX: 32,
    borderRadius: 8,
    height: 64,
  },
}

const variantStyles: Record<
  Variant,
  {
    styles: CommonBoxProps
    ':hover': CommonBoxProps
  }
> = {
  primary: {
    styles: {
      color: 'white',
      backgroundColor: 'purple500',
    },
    ':hover': {
      color: 'gray100',
      backgroundColor: 'purple600',
    },
  },
  secondary: {
    styles: {
      color: 'black900',
      backgroundColor: 'white',
    },
    ':hover': {
      backgroundColor: 'gray100',
    },
  },
  secondaryWithBorder: {
    styles: {
      color: 'white',
      //@ts-ignore -- This works
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: 'purple',
    },
    ':hover': {
      color: 'gray100',
      borderColor: 'hoverPurple',
    },
  },
  lightSecondaryWithBorder: {
    styles: {
      color: 'black900',
      //@ts-ignore -- This works
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: 'purple',
    },
    ':hover': {
      borderColor: 'hoverPurple',
    },
  },
  dark: {
    styles: {
      color: 'white',
      backgroundColor: 'darkButton',
    },
    ':hover': {
      color: 'gray100',
      backgroundColor: 'darkButton',
    },
  },
  ghost: {
    styles: {
      color: 'black900',
      backgroundColor: 'transparent',
    },
    ':hover': {
      backgroundColor: 'hoverHighlight',
    },
  },
  lightGhost: {
    styles: {
      color: 'white',
      backgroundColor: 'transparent',
    },
    ':hover': {
      color: 'white',
      backgroundColor: 'lightHighlight',
    },
  },
  link: {
    styles: {
      backgroundColor: 'transparent',
      paddingBottom: 0,
      fontWeight: 500,
      height: 24,
      //@ts-ignore -- This works
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      borderBottomColor: 'linkUnderline',
      color: 'black',
      transition: 'border-bottom-color 0.2s ease, color 0.2s ease',
    },
    ':hover': {
      backgroundColor: 'transparent',
      borderBottomColor: 'linkUnderlineHover',
      color: 'black900',
    },
  },
  leadLink: {
    styles: {
      fontSize: 20,
      lineHeight: '150%',
      fontWeight: 500,
      color: 'dataPink',
      backgroundColor: 'transparent',
      textDecorationColor: 'purple500',
      textUnderlineOffset: 6,
    },
    ':hover': {
      color: 'dataPink',
      backgroundColor: 'transparent',
      transform: 'none',
      textDecoration: 'underline',
    },
  },
  hoverHighlight: {
    styles: {
      borderRadius: 16,
      transition: 'none',
    },
    ':hover': {
      backgroundColor: 'blue100',
      transform: 'none',
    },
  },
  darkHoverHighlight: {
    styles: {
      color: 'white',
      borderRadius: 12,
      transition: 'none',
    },
    ':hover': {
      color: 'white',
      backgroundColor: 'rgba(255, 255, 255, 0.08)' as BoxProps['color'],
      transform: 'none',
    },
  },
  darkMenuLink: {
    styles: {
      color: 'darkMenuText',
      transition: 'none',
    },
    ':hover': {
      color: 'white',
      transform: 'none',
    },
  },
  underlineLink: {
    styles: {
      textDecoration: 'none',
    },
    ':hover': {
      textDecoration: 'underline',
    },
  },
  basicLink: {
    styles: {
      textDecoration: 'underline',
    },
    ':hover': {
      textDecoration: 'underline',
    },
  },
  muted: {
    styles: {
      color: 'black600',
    },
    ':hover': {
      color: 'black900',
    },
  },
  plain: {
    styles: {
      //@ts-ignore
      height: 'auto',
    },
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
}

const disabledStyles: CommonBoxProps = {
  opacity: 0.6,
  disabled: true,
  cursor: 'not-allowed',
}
export default forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'secondary',
      disabled,
      size = 'medium',
      children,
      href,
      block,
      // Default to "button" to prevent unintentional <form> submits
      type = 'button',
      font,
      hoverProps: customHoverProps,
      arrow,
      rightCaret,
      ...rest
    },
    ref,
  ) {
    const dynamicButtonProps: CommonBoxProps = {
      //@ts-ignore -- This works, our box typings are just off.
      width: block ? '100%' : 'auto',
    }

    const hasLinkStyles = [
      'leadLink',
      'hoverHighlight',
      'link',
      'underlineLink',
      'basicLink',
      'muted',
      'plain',
    ].includes(variant)

    const elementProps = hasLinkStyles
      ? anchorProps
      : ({
          ...buttonProps,
          ...dynamicButtonProps,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- not worth debugging the types
          ...transformResponsiveProp(size, buttonSizeStyles as any),
        } as CommonBoxProps)

    const currentVariant = variantStyles[variant]

    // We need to spread the individual props from fontVariant in order for the overrides to work
    const fontProps = font ? fontVariants[font] : {}
    const hoverProps = {
      transform: hasLinkStyles ? 'none' : 'scale3d(0.98, 0.98, 0.98)',
      transition: 'all 0.15s ease-out',
      ...currentVariant[':hover'],
      ...(customHoverProps ?? {}),
      theme,
    }

    const props = {
      ...commonProps,
      ...elementProps,
      ...currentVariant.styles,
      ...(disabled ? disabledStyles : {}),
      disabled,
      children: arrow ? (
        <>
          {children}
          <Box.span
            className="arrow"
            display="inline-block"
            left={0}
            position="relative"
            // @ts-expect-error
            fontSize="inherit"
            paddingLeft={hasLinkStyles ? 8 : 24}
            verticalAlign="middle"
            // Use a smaller line-height to prevent the link becoming
            // bigger with "verticalAlign=middle"
            lineHeight="20px"
            transition="all 0.15s ease-out 0s"
            css={hasLinkStyles ? '' : 'margin-right: -6px;'}
          >
            &rarr;
          </Box.span>
        </>
      ) : rightCaret ? (
        <Row
          width="100%"
          display="flex"
          alignContent="center"
          justifyContent="space-between"
        >
          <Box>{children}</Box>{' '}
          <Box.img className="arrow-right" src={ArrowRightIcon} width={9} />
          <Box.img
            className="arrow-right-dark"
            src={ArrowRightDarkIcon}
            width={9}
          />
        </Row>
      ) : (
        children
      ),
      ...fontProps,
      ...rest,
    }

    // CSS styles need to be defined separately from props and included directly in JSX. They won't otherwise work.
    const cssStyles = propsToCss({
      '&:hover:not([disabled])': hoverProps,
      '&:hover .arrow': {
        left: 2,
      },
      '.arrow-right': {
        display: 'inline',
      },
      '&:hover .arrow-right': {
        display: 'none',
      },
      '.arrow-right-dark': {
        display: 'none',
      },
      '&:hover .arrow-right-dark': {
        display: 'inline',
      },
    })

    // For some reason our tooling does not allow us to do `const Component = href ? Box.a : Box.button`.
    // So we need to go with this hack.
    const isInternalLink = href && href.startsWith('/')
    return isInternalLink ? (
      <Box.Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        {...props}
        // @ts-ignore -- Works, but the typings are just a bit off
        to={href}
        css={cssStyles}
        onClick={event => {
          // Disabled clicks for links manually
          if (disabled) {
            event.preventDefault()
            return
          }
          rest.onClick?.(event)
        }}
      />
    ) : href ? (
      <Box.a
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        {...props}
        href={href}
        css={cssStyles}
        onClick={event => {
          // Disabled clicks for links manually
          if (disabled) {
            event.preventDefault()
            return
          }
          rest.onClick?.(event)
        }}
      />
    ) : (
      <Box.button
        ref={ref as ForwardedRef<HTMLButtonElement>}
        {...props}
        type={type}
        css={cssStyles}
      />
    )
  },
)
